import type { ClosingPackageWorkflowState, CompositionMode } from '../types';

export type CompositionReadOnlyReason =
  | 'NO_PERMISSION'
  | 'LOCKED'
  | 'FORMED'
  | 'WORKFLOW_READONLY'
  | 'RECOMMENDED';

const EDITABLE_WORKFLOW_STATES = new Set<ClosingPackageWorkflowState>(['DRAFT', 'REVISION_REQUIRED']);

export type CompositionEditGateInput = {
  /** Combined permission + role flag from hook — EditGate does not inspect roles. */
  allowedToEdit: boolean;
  locked: boolean;
  formed: boolean;
  compositionMode: CompositionMode;
  workflowState: ClosingPackageWorkflowState;
};

export type CompositionEditGateResult = {
  canEdit: boolean;
  readOnlyReason: CompositionReadOnlyReason | null;
  isLocked: boolean;
  isFormed: boolean;
};

/**
 * Pure edit gate — no React, Redux, or RTK.
 * Single authority for composition editor mutability preconditions.
 */
export function resolveCompositionEditGate(input: CompositionEditGateInput): CompositionEditGateResult {
  const isLocked = input.locked === true;
  const isFormed = input.formed === true;
  const workflowEditable = EDITABLE_WORKFLOW_STATES.has(input.workflowState);
  const isRecommended = input.compositionMode === 'RECOMMENDED';

  if (!input.allowedToEdit) {
    return { canEdit: false, readOnlyReason: 'NO_PERMISSION', isLocked, isFormed };
  }
  if (isLocked) {
    return { canEdit: false, readOnlyReason: 'LOCKED', isLocked, isFormed };
  }
  if (isFormed) {
    return { canEdit: false, readOnlyReason: 'FORMED', isLocked, isFormed };
  }
  if (!workflowEditable) {
    return { canEdit: false, readOnlyReason: 'WORKFLOW_READONLY', isLocked, isFormed };
  }
  if (isRecommended) {
    return { canEdit: false, readOnlyReason: 'RECOMMENDED', isLocked, isFormed };
  }

  return { canEdit: true, readOnlyReason: null, isLocked, isFormed };
}
